import { Component, Input } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { EnvService } from '../core/services';
import { Select, Store } from '@ngxs/store';
import * as moment from 'moment';
import { ApiStoreData } from '../core/store/utils';
import { Observable, withLatestFrom } from 'rxjs';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { PatientState } from '../core/store/patient/patient.state';
import { UpdatePatient } from '../core/store/patient/patient.actions';
import { Patient } from '../core/models';
import { maxDateValidator, minDateValidator, zipCodeValidator } from '../core/utils/validators';

@Component({
  selector: 'app-hipaa-form',
  templateUrl: './hipaa-form.component.html',
  styleUrls: ['./hipaa-form.component.scss'],
})
export class HipaaFormComponent {
  @Input() displayHipaaVerifyModal = false;
  @Input() afterVerify: () => void = () => {};
  @Select(PatientState.updatePatientState) updatePatientState$!: Observable<ApiStoreData<null>>;
  @Select(PatientState.patient) patient$!: Observable<Patient>;
  termsAndConditionsUrl = this.envService.ccs.termsAndConditionsUrl;
  privacyUrl = this.envService.ccs.privacyUrl;
  helpPhone = this.envService.ccs.helpPhone;
  minDob = moment('1900-01-01').startOf('day');
  maxDob = moment().startOf('day');

  hipaaForm = new FormGroup({
    lastName: new FormControl(null, Validators.required),
    zipCode: new FormControl(null, [Validators.required, zipCodeValidator()]),
    dob: new FormControl(null, [Validators.required, minDateValidator(this.minDob), maxDateValidator(this.maxDob)]),
    acceptedTermsAndConditions: new FormControl(false, Validators.requiredTrue),
    consentedToMarketing: new FormControl(false),
  });

  errorMessage: string | null = null;

  constructor(
    private envService: EnvService,
    private store: Store,
    private router: Router,
    private messageService: MessageService
  ) {}

  onSubmit() {
    if (!this.hipaaForm.valid) {
      this.hipaaForm.markAllAsTouched();
      return;
    }

    this.errorMessage = null;

    const payload = {
      hipaa: {
        lastName: this.hipaaForm.value.lastName,
        zipCode: this.hipaaForm.value.zipCode,
        dob: moment(this.hipaaForm.value.dob).format('YYYY-MM-DD'),
      },
      acceptedTermsAndConditions: this.hipaaForm.value.acceptedTermsAndConditions,
      consentedToMarketing: this.hipaaForm.value.consentedToMarketing,
    };

    this.store
      .dispatch(new UpdatePatient(payload, { optimisticUpdate: false }))
      .pipe(withLatestFrom(this.updatePatientState$))
      .subscribe(([_, updatePatientState]) => {
        if (updatePatientState.error) {
          this.errorMessage = `It looks like the information you entered doesn't match our records. Please double-check and try again. If you need help, please call us at ${this.helpPhone}.`;
        } else {
          this.afterVerify();
          this.router.navigate(['/']);
          window.setTimeout(() => {
            this.messageService.add({
              key: 'homeMessages',
              severity: 'success',
              detail: 'You have completed your validation.',
              closable: true,
            });
          }, 500);
        }
      });
  }

  asFormControl(a: AbstractControl | null | undefined) {
    return a ? (a as FormControl) : new FormControl(null);
  }
}
